import { env } from '@/shared/config/env';
import {
  adaptBcv,
  adaptBcvHistorico,
  adaptCapacidades,
  adaptFuentes,
  adaptFuentesDetalle,
  adaptHistorico,
  adaptHistoricoSeries,
  adaptResumen,
  adaptRubros,
  adaptUltimos,
  adaptVariaciones,
} from '@/shared/domain/adapters';
import type {
  Capacidades,
  Fuente,
  FuenteDetalle,
  Historico,
  ISODate,
  ResumenMercado,
  RubroInfo,
  TasaBcv,
  UltimosData,
  VariacionesResultado,
} from '@/shared/domain/types';
import { apiAxios } from '../api';
import { qs, toResult } from '../handlers';
import type { ApiResult } from '../types';
import { pub, type RequestOpts } from './common';

/** Servicios públicos de mercado (API original + analítica del add-on). Nunca lanzan: devuelven ApiResult. */
export interface UltimosParams {
  rubro?: string;
  region?: string;
  fuente?: string;
}

export interface HistoricoParams {
  /** Obligatorio en la API. */
  rubro: string;
  categoria?: string;
  presentacion?: string | null;
  desde?: ISODate;
  hasta?: ISODate;
  fuente?: string;
  limit?: number;
}

export interface VariacionesParams {
  periodoDias?: number;
  rubro?: string;
  categoria?: string;
  presentacion?: string | null;
  region?: string;
  fuente?: string;
  limite?: number;
}

const query = (p: HistoricoParams): string =>
  qs({
    rubro: p.rubro,
    categoria: p.categoria,
    presentacion: p.presentacion,
    desde: p.desde,
    hasta: p.hasta,
    fuente: p.fuente,
    limit: p.limit,
  });

/** Base absoluta de la API (axios antepone baseURL a las rutas relativas, así que /api/health no sirve). */
function healthBase(): string {
  const base = env.apiBaseUrl;
  return /^https?:\/\//i.test(base) ? base : `${globalThis.location?.origin ?? ''}${base}`;
}

export const marketApi = {
  /** GET /health (fuera de /v1). Solo importa el código HTTP: ok = la API responde. */
  getHealth: (opts?: RequestOpts): Promise<ApiResult<boolean>> =>
    toResult(apiAxios.get({ url: `${healthBase()}/health`, config: pub(opts) }), () => true),

  getUltimos: (params: UltimosParams = {}, opts?: RequestOpts): Promise<ApiResult<UltimosData>> =>
    toResult(
      apiAxios.get({ url: `/precios/ultimos${qs({ ...params })}`, config: pub(opts) }),
      adaptUltimos,
    ),

  /** Una sola serie: la pedida (categoría y presentación) o, si no se fijan, todas unidas. */
  getHistorico: (params: HistoricoParams, opts?: RequestOpts): Promise<ApiResult<Historico>> =>
    toResult(
      apiAxios.get({ url: `/precios/historico${query(params)}`, config: pub(opts) }),
      (raw) =>
        adaptHistorico(raw, {
          rubro: params.rubro,
          categoria: params.categoria,
          presentacion: params.presentacion,
        }),
    ),

  /** Todas las series de un rubro por separado (para calcular variaciones en el cliente). */
  getHistoricoSeries: (
    params: HistoricoParams,
    opts?: RequestOpts,
  ): Promise<ApiResult<Historico[]>> =>
    toResult(
      apiAxios.get({ url: `/precios/historico${query(params)}`, config: pub(opts) }),
      (raw) => adaptHistoricoSeries(raw, { rubro: params.rubro }).data,
    ),

  getRubros: (opts?: RequestOpts): Promise<ApiResult<RubroInfo[]>> =>
    toResult(apiAxios.get({ url: '/rubros', config: pub(opts) }), adaptRubros),

  getFuentes: (opts?: RequestOpts): Promise<ApiResult<Fuente[]>> =>
    toResult(apiAxios.get({ url: '/fuentes', config: pub(opts) }), adaptFuentes),

  /** Tasa del día, o la vigente en `fecha`. */
  getBcv: (fecha?: ISODate, opts?: RequestOpts): Promise<ApiResult<TasaBcv>> =>
    toResult(apiAxios.get({ url: `/bcv${qs({ fecha })}`, config: pub(opts) }), adaptBcv),

  getBcvHistorico: (desde?: ISODate, opts?: RequestOpts): Promise<ApiResult<TasaBcv[]>> =>
    toResult(
      apiAxios.get({ url: `/bcv/historico${qs({ desde })}`, config: pub(opts) }),
      adaptBcvHistorico,
    ),

  /** 404 = la API no tiene el add-on (modo básico). */
  getCapacidades: (opts?: RequestOpts): Promise<ApiResult<Capacidades>> =>
    toResult(apiAxios.get({ url: '/capacidades', config: pub(opts) }), adaptCapacidades),

  getVariaciones: (
    params: VariacionesParams = {},
    opts?: RequestOpts,
  ): Promise<ApiResult<VariacionesResultado>> =>
    toResult(
      apiAxios.get({
        url: `/mercado/variaciones${qs({
          periodo: params.periodoDias,
          rubro: params.rubro,
          categoria: params.categoria,
          presentacion: params.presentacion,
          region: params.region,
          fuente: params.fuente,
          limite: params.limite,
        })}`,
        config: pub(opts),
      }),
      adaptVariaciones,
    ),

  getResumen: (opts?: RequestOpts): Promise<ApiResult<ResumenMercado>> =>
    toResult(apiAxios.get({ url: '/mercado/resumen', config: pub(opts) }), adaptResumen),

  getFuentesDetalle: (opts?: RequestOpts): Promise<ApiResult<FuenteDetalle[]>> =>
    toResult(apiAxios.get({ url: '/fuentes/detalle', config: pub(opts) }), adaptFuentesDetalle),
};
